import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, DocumentText1, Status, Refresh2 } from "iconsax-react";
import { Select, Input, DatePicker } from "@/shared/ui";
import { LegalDocumentType } from "@/features/legal/types/documentTypes";
import type { DocumentTypeOption } from "@/features/legal/hooks";
import type { StatusOption } from "@/features/legal/shared/utils";
import type { Locale, DateRange } from "@/shared/utils";

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
  documentTypeFilter: LegalDocumentType | null;
  onDocumentTypeChange: (v: LegalDocumentType | null) => void;
  statusFilter: string | null;
  onStatusChange: (v: string | null) => void;
  documentTypeOptions: DocumentTypeOption[];
  statusOptions: StatusOption[];
  hasActiveFilters: boolean;
  onReset: () => void;
  locale: Locale;
}

export default function ApplicationsFilter({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  documentTypeFilter,
  onDocumentTypeChange,
  statusFilter,
  onStatusChange,
  documentTypeOptions,
  statusOptions,
  hasActiveFilters,
  onReset,
  locale,
}: Props) {
  const { t } = useTranslation("LegalApplicationsPage");

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onDateRangeChange(v);
  }

  return (
    <div className="flex items-center gap-2 mb-7">
      <div className="flex items-center gap-2">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={dateRange}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.allTime")}
          isSmall
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-40">
          <Select<LegalDocumentType | null>
            options={documentTypeOptions}
            placeholder={t("filters.allDocumentTypes")}
            value={documentTypeFilter}
            onChange={onDocumentTypeChange}
            isSmall
            icon={<DocumentText1 size={16} color="currentColor" />}
            width="w-72"
          />
        </div>

        <div className="min-w-40">
          <Select<string | null>
            options={statusOptions}
            placeholder={t("filters.allStatuses")}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            icon={<Status size={16} color="currentColor" />}
            width="w-72"
          />
        </div>

        {hasActiveFilters && (
          <button
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onReset}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>
    </div>
  );
}
